// machines.js — 機種データ（純粋データ / ブラウザ API 非依存）
//
// 盤面のジオメトリは全機種で共通だが、釘調整・確率・ラウンド数・払い出しが
// 変わることで手触りが大きく変わる。nailGap は実測で
//   13.6px → ヘソ 5.3% / 17px → 8.1% / 21px → 11.1%
// あたりになる（scripts/simulate.mjs で確認できる）。
//
// 名前や説明はここには置かない。言語ごとに変わるので js/locales/ にあり、
// ここが持つのは id と数値だけ。表示するときは machineName() などを通す。

import { t } from './i18n.js';

export const NAIL_KINDS = ['tight', 'normal', 'loose'];
export const nailLabel = (kind) => t(`nail.${kind}`);

// 通貨。ホールの正規の台は玉で遊ぶが、自作台は玉とは別勘定の「メダル」で遊ぶ。
// 財布を分けておくと、釘を好きに触れる自作台の出玉が正規の台へ流れ込まない。
// 「イン」は景品を換金所で換えたときに手に入る、店の外だけで使えるお金。
export const CURRENCIES = {
  ball: { id: 'ball', start: 800, service: 100 },
  medal: { id: 'medal', start: 500, service: 80 },
  ing: { id: 'ing', start: 0, service: 0 },
};

export const currencyOf = (m) => CURRENCIES[m?.currency] || CURRENCIES.ball;
export const currencyLabel = (c) => t(`currency.${c.id || c}.label`);

// 自作台の名前は利用者が入れたものなので、そのまま出す（訳す相手ではない）
export const machineName = (m) => (m.custom ? m.name : t(`machine.${m.id}.name`));
export const machineShort = (m) => (m.custom ? m.short : t(`machine.${m.id}.short`));
export const machineType = (m) => (m.custom ? m.type : t(`machine.${m.id}.type`));
export const machineBlurb = (m) => (m.custom ? m.blurb : t(`machine.${m.id}.blurb`));

export const MACHINES = [
  {
    id: 'neon',
    icon: '◆',
    hue: 195,
    color: '#4de0ff',
    accent: '#ff4d8d',
    odds: 45,
    rounds: 10,
    premiumRounds: 16,
    reachChance: 0.11,
    superReachChance: 0.35,
    roundMaxBalls: 8,
    roundTime: 1200,
    fireInterval: 28,
    nailGap: 17,
    nail: 'normal',
    awards: { start: 3, side: 2, attacker: 14 },
  },
  {
    id: 'light',
    icon: '★',
    hue: 145,
    color: '#5cdb95',
    accent: '#ffd166',
    odds: 19,
    rounds: 4,
    premiumRounds: 6,
    reachChance: 0.16,
    superReachChance: 0.28,
    roundMaxBalls: 8,
    roundTime: 1200,
    fireInterval: 28,
    nailGap: 21,
    nail: 'loose',
    awards: { start: 3, side: 2, attacker: 11 },
  },
  {
    id: 'max',
    icon: '7',
    hue: 350,
    color: '#ff4d6d',
    accent: '#ffd166',
    odds: 99,
    rounds: 16,
    premiumRounds: 16,
    reachChance: 0.09,
    superReachChance: 0.5,
    roundMaxBalls: 9,
    roundTime: 1400,
    fireInterval: 26,
    nailGap: 14,
    nail: 'tight',
    awards: { start: 3, side: 2, attacker: 15 },
  },
  {
    id: 'rush',
    icon: '☆',
    hue: 275,
    color: '#c77dff',
    accent: '#4de0ff',
    odds: 60,
    rounds: 12,
    premiumRounds: 16,
    reachChance: 0.26,
    superReachChance: 0.45,
    roundMaxBalls: 8,
    roundTime: 1200,
    fireInterval: 24,
    nailGap: 17,
    nail: 'normal',
    awards: { start: 3, side: 2, attacker: 14 },
  },
  {
    id: 'cinema',
    icon: '◉',
    hue: 320,
    color: '#ff5fb0',
    accent: '#ffd166',
    odds: 79,
    rounds: 12,
    premiumRounds: 16,
    reachChance: 0.20,
    superReachChance: 0.5,
    roundMaxBalls: 8,
    roundTime: 1300,
    fireInterval: 26,
    nailGap: 16,
    nail: 'normal',
    awards: { start: 3, side: 2, attacker: 14 },
    // 液晶に流す映像。状態ごとに差し替わる（js/video.js）
    video: {
      idle: 'videos/wait.mp4',
      spin: 'videos/kaiten.mp4',
      reach: 'videos/reach.mp4',
      jackpot: 'videos/reach.mp4',
    },
  },
  {
    id: 'retro',
    icon: '3',
    hue: 40,
    color: '#ffb454',
    accent: '#5cdb95',
    odds: 30,
    rounds: 6,
    premiumRounds: 10,
    reachChance: 0.13,
    superReachChance: 0.3,
    roundMaxBalls: 7,
    roundTime: 1100,
    fireInterval: 30,
    nailGap: 19,
    nail: 'loose',
    awards: { start: 4, side: 2, attacker: 13 },
  },
];

export const DEFAULT_MACHINE = MACHINES[0];

export function findMachine(id) {
  return MACHINES.find((m) => m.id === id) || DEFAULT_MACHINE;
}

// 1 回の大当りでおおよそ何発出るか（ラウンド消化ぶんの純増の目安）。
// ホールの台選びで比較できるように表示する。
export function estimatePayout(m) {
  return Math.round(m.rounds * m.roundMaxBalls * m.awards.attacker * 0.72);
}
